import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import ScrollReveal from '../components/ScrollReveal'
import StructuredData from '../components/StructuredData'
import CTASection from '../components/CTASection'
import LatestNewsPost from '../components/LatestNewsPost'
import './Home.css'

const companyTypes = [
  {
    title: 'Early-Stage Startups',
    description: 'Seed to Series A companies with non-technical founders who need to build their first engineering team, establish technical culture, and make foundational architecture decisions.',
  },
  {
    title: 'Healthcare & Wellness Practices',
    description: 'Multi-location medical practices, therapy networks, and wellness centres navigating data protection requirements, patient management systems, and the integration of billing platforms.',
  },
  {
    title: 'E-commerce & Retail Brands',
    description: 'Direct-to-consumer brands wrestling with platform decisions, inventory systems, fulfilment integrations, and customer data architecture.',
  },
  {
    title: 'Manufacturing & Distribution',
    description: 'Regional manufacturers and distributors modernising legacy systems, implementing ERP solutions, and building digital capabilities to remain competitive.',
  },
  {
    title: 'Creative & Media Agencies',
    description: 'Design studios, marketing agencies, and production companies managing complex project workflows, digital asset systems, and client collaboration platforms.',
  },
  {
    title: 'Professional Services Firms',
    description: 'Consultancies, accountancy firms, and law firms with employees managing client data, regulatory requirements, and operational systems that have grown organically without strategic oversight.',
  },
]

const benefits = [
  {
    number: '01',
    title: 'Cost Efficiency',
    description: "Access executive-calibre leadership at 10–40% of a full-time CTO's total compensation, redirecting capital toward growth initiatives and technical implementation.",
  },
  {
    number: '02',
    title: 'Immediate Expertise',
    description: 'Skip the six-month learning curve. A fractional CTO brings pattern recognition from dozens of engagements, identifying opportunities and risks faster.',
  },
  {
    number: '03',
    title: 'Objective Perspective',
    description: 'External positioning enables honest assessment of technical debt, team capabilities, and vendor relationships without political entanglement.',
  },
  {
    number: '04',
    title: 'Flexible Commitment',
    description: 'Scale engagement intensity to match business cycles—increase involvement during launches or crises, reduce during stable periods.',
  },
  {
    number: '05',
    title: 'Network Access',
    description: 'Leverage established relationships with vetted agencies, contractors, and technology vendors cultivated across years of industry practice.',
  },
  {
    number: '06',
    title: 'Risk Mitigation',
    description: 'Reduce costly technical decisions, security vulnerabilities, and vendor lock-in through experienced due diligence and governance frameworks.',
  },
]

const deliverables = [
  { title: 'Technology Roadmap', description: 'A prioritised 12–24 month plan aligning technical initiatives with business objectives, including resource requirements and decision milestones.' },
  { title: 'Architecture Review', description: 'Comprehensive assessment of current systems, identifying technical debt, security gaps, scalability constraints, and modernisation opportunities.' },
  { title: 'Vendor Evaluation', description: 'Objective analysis of technology partners and platforms with negotiation support, contract review, and implementation oversight.' },
  { title: 'Team Building', description: 'Hiring strategy, technical interview support, capability assessment, and onboarding processes that set new hires up to succeed.' },
  { title: 'Process Design', description: "Development workflows, documentation standards, and operational procedures scaled appropriately for your organisation's maturity." },
  { title: 'Board & Investor Communications', description: 'Technical due diligence preparation, investor updates, and board presentations that translate complexity into clarity.' },
  { title: 'Security & Compliance', description: 'Risk assessments, policy development, and certification support for Cyber Essentials, ISO 27001, GDPR, and industry-specific requirements.' },
  { title: 'Crisis Response', description: 'Rapid assessment and remediation during technical emergencies, outages, or security incidents with post-mortem analysis.' },
  { title: 'Digital Transformation', description: 'Strategic guidance for legacy system modernisation, cloud migration, and operational digitisation initiatives.' },
  { title: 'Website & Application Design', description: 'From marketing sites to customer portals - design, specification, and build oversight ensuring the result matches the brief and the budget.' },
  { title: 'System Integrations', description: 'Connecting your CRM, accounting, inventory, and operational tools so data flows automatically and your team stops copying between spreadsheets.' },
  { title: 'Workflow Automation', description: 'Identifying repetitive manual processes and implementing automation - from simple Zapier flows to custom integrations that save hours weekly.' },
  { title: 'Cloud Infrastructure', description: 'AWS, Google or alternative cloud setup and optimisation - right-sized for your actual needs, and designed with security.' },
  { title: 'Data & Analytics', description: 'Dashboards, reporting pipelines, and data architecture that turn scattered information into actionable insight.' },
]

function Home() {
  return (
    <>
      <SEO 
        title="Claverton 42 - Fractional CTO Services in the UK" 
        description="Fractional CTO services for growing companies. Executive-level technology leadership, security guidance, and strategic planning - engaged precisely when you need it."
        path="/"
      />
      <StructuredData type="website" />
      
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg-image" />
        <div className="hero-content">
          <h2 className="hero-label">Fractional CTO Services</h2>
          <h1>Strategic <em>technology</em> leadership, without the overhead</h1>
          <p className="hero-description">
            Executive-level technical guidance for companies ready to scale
            thoughtfully - engaged precisely when and how you need it.
          </p>
          <p className="hero-description">
            Claverton 42 provides fractional CTO and interim technology leadership
            services to UK startups, scaleups, and established organisations.
          </p>
          <div className="scroll-indicator">
            <span className="scroll-line"></span>
            <Link to="/contact">Get in touch <span className="arrow">➤</span></Link>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-graphic" />
        </div>
      </section>

      {/* What Section */}
      <section id="what" className="what-section">
        <div className="section-grid">
          <div className="section-label">
            What
            <span></span>
          </div>
          <ScrollReveal className="what-content">
            <h2>
              A fractional CTO provides
              <em> executive technology leadership</em> on a part-time or temporary
              basis
            </h2>
            <div className="what-text">
              <p>
                You gain the strategic insight of a seasoned technology
                executive—someone who has built and scaled engineering teams,
                architected enterprise systems, and navigated the complex
                intersection of technology and business - without committing to a
                full-time salary.
              </p>
              <p>
                This model is designed for growing companies that have outpaced
                DIY solutions but aren't yet ready for a six-figure executive
                hire. The engagement flexes with your needs; intensive during
                critical periods, lighter during steady-state operations.
              </p>
              <p>
                The result is informed decision-making at every level-from daily
                technical choices to multi-year roadmap development, grounded in
                decades of real-world experience.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Who Section */}
      <section id="who" className="who-section">
        <div className="section-grid">
          <div className="section-label">
            Who
            <span></span>
          </div>
          <div className="who-content">
            <ScrollReveal>
              <h2>
                Organisations that benefit most from fractional technology
                leadership
              </h2>
            </ScrollReveal>
            <div className="company-types">
              {companyTypes.map((type, index) => (
                <ScrollReveal key={type.title} delay={index * 100}>
                  <div className="company-type">
                    <h3>{type.title}</h3>
                    <p>{type.description}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="benefits-section">
        <div className="section-grid">
          <div className="section-label">
            Benefits
            <span></span>
          </div>
          <div className="benefits-content">
            <ScrollReveal>
              <h2>The strategic advantages of fractional engagement</h2>
            </ScrollReveal>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <ScrollReveal key={benefit.number} delay={index * 100}>
                  <div className="benefit-item">
                    <span className="benefit-number">{benefit.number}</span>
                    <div className="benefit-text">
                      <h3>{benefit.title}</h3>
                      <p>{benefit.description}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables Section */}
      <section id="deliverables" className="deliverables-section">
        <div className="section-grid">
          <div className="section-label">
            Deliverables
            <span></span>
          </div>
          <div className="deliverables-content">
            <ScrollReveal>
              <h2>Tangible outcomes from every engagement</h2>
            </ScrollReveal>
            <ScrollReveal>
              <p className="deliverables-intro">
                The work product is concrete, actionable, and designed to outlast
                the engagement itself.
              </p>
            </ScrollReveal>
            <div className="deliverables-list">
              {deliverables.map((item, index) => (
                <ScrollReveal key={item.title} delay={index * 50}>
                  <div className="deliverable">
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Latest News Post Section */}
      <LatestNewsPost />

      {/* CTA Section */}
      <CTASection />
    </>
  )
}

export default Home
